import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Users, Pencil, Plus } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  mobile: string | null;
  enabled: boolean;
  admin: boolean;
  super_admin: boolean;
  client_id: string | null;
  created_at: string;
}

interface Client {
  id: string;
  name: string;
}

type RoleValue = "super_admin" | "admin" | "user";

const roleToFields = (role: RoleValue) => ({
  super_admin: role === "super_admin",
  admin: role === "admin",
});

const profileToRole = (p: Profile): RoleValue => {
  if (p.super_admin) return "super_admin";
  if (p.admin) return "admin";
  return "user";
};

const Profiles = () => {
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [editName, setEditName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editRole, setEditRole] = useState<RoleValue>("user");
  const [editSaving, setEditSaving] = useState(false);

  // Add profile state
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addPassword, setAddPassword] = useState("");
  const [addMobile, setAddMobile] = useState("");
  const [addClientId, setAddClientId] = useState("");
  const [addRole, setAddRole] = useState<RoleValue>("user");
  const [addSaving, setAddSaving] = useState(false);

  const fetchData = async () => {
    const [profilesRes, clientsRes] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, full_name, email, mobile, enabled, admin, super_admin, client_id, created_at")
        .eq("hide_from_ui", false)
        .order("created_at", { ascending: false }),
      supabase.from("client").select("id, name").order("name"),
    ]);

    if (profilesRes.error) {
      toast({ title: "Error", description: profilesRes.error.message, variant: "destructive" });
    } else {
      setProfiles(profilesRes.data ?? []);
    }

    if (clientsRes.error) {
      toast({ title: "Error", description: clientsRes.error.message, variant: "destructive" });
    } else {
      setClients(clientsRes.data ?? []);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleEnabled = async (profile: Profile) => {
    setUpdating(profile.id);
    const { error } = await supabase.from("profiles").update({ enabled: !profile.enabled }).eq("id", profile.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setProfiles((prev) => prev.map((p) => (p.id === profile.id ? { ...p, enabled: !p.enabled } : p)));
    }
    setUpdating(null);
  };

  const assignClient = async (profileId: string, clientId: string) => {
    setUpdating(profileId);
    const value = clientId === "__none__" ? null : clientId;
    const { error } = await supabase.from("profiles").update({ client_id: value }).eq("id", profileId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setProfiles((prev) => prev.map((p) => (p.id === profileId ? { ...p, client_id: value } : p)));
    }
    setUpdating(null);
  };

  const openEdit = (profile: Profile) => {
    setEditingProfile(profile);
    setEditName(profile.full_name ?? "");
    setEditMobile(profile.mobile ?? "");
    setEditRole(profileToRole(profile));
  };

  const saveEdit = async () => {
    if (!editingProfile) return;
    setEditSaving(true);
    const fields = roleToFields(editRole);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: editName.trim() || null, mobile: editMobile.trim() || null, ...fields })
      .eq("id", editingProfile.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === editingProfile.id
            ? { ...p, full_name: editName.trim() || null, mobile: editMobile.trim() || null, ...fields }
            : p
        )
      );
      toast({ title: "Profile updated" });
      setEditingProfile(null);
    }
    setEditSaving(false);
  };

  const getClientName = (clientId: string | null) => clients.find((c) => c.id === clientId)?.name ?? null;

  const resetAddForm = () => {
    setAddName("");
    setAddEmail("");
    setAddPassword("");
    setAddMobile("");
    setAddClientId("");
    setAddRole("user");
  };

  const handleAddProfile = async () => {
    if (!addName.trim() || !addEmail.trim() || !addPassword || !addClientId) {
      toast({ title: "Error", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    setAddSaving(true);
    try {
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-profile`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            email: addEmail.trim(),
            password: addPassword,
            full_name: addName.trim(),
            mobile: addMobile.trim() || null,
            client_id: addClientId,
            role: addRole,
          }),
        }
      );
      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error || "Failed to create profile");
      }
      toast({ title: "Profile created", description: `${addName.trim()} has been added and can now chat on WhatsApp.` });
      setAddOpen(false);
      resetAddForm();
      await fetchData();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setAddSaving(false);
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Profiles</h1>
          <p className="text-sm text-muted-foreground">
            Manage user profiles — edit name, role, enable/disable access and assign clients.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Profile
        </Button>
      </div>

      {profiles.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No profiles yet</CardTitle>
          <CardDescription>Profiles will appear here once users sign up.</CardDescription>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-center">Enabled</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">
                    {p.full_name || <span className="text-muted-foreground italic">Unnamed</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.email || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.mobile || <span className="italic">—</span>}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={p.client_id ?? "__none__"}
                      onValueChange={(v) => assignClient(p.id, v)}
                      disabled={updating === p.id}
                    >
                      <SelectTrigger className="w-44 h-8 text-sm">
                        <SelectValue>
                          {p.client_id ? (getClientName(p.client_id) ?? "Unknown") : "No client"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No client</SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.super_admin ? "Super Admin" : p.admin ? "Admin" : "User"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(p.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch checked={p.enabled} onCheckedChange={() => toggleEnabled(p)} disabled={updating === p.id} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!editingProfile} onOpenChange={(open) => { if (!open) setEditingProfile(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="edit-mobile">Mobile</Label>
              <Input
                id="edit-mobile"
                value={editMobile}
                onChange={(e) => setEditMobile(e.target.value)}
                placeholder="Mobile number"
                type="tel"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editRole} onValueChange={(v) => setEditRole(v as RoleValue)}>
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProfile(null)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={editSaving}>
              {editSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Profile Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => { if (!open) { setAddOpen(false); resetAddForm(); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Profile</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="add-name">Full Name *</Label>
              <Input id="add-name" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Full name" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-email">Email *</Label>
              <Input id="add-email" type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="email@example.com" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-password">Password *</Label>
              <Input id="add-password" type="password" value={addPassword} onChange={(e) => setAddPassword(e.target.value)} placeholder="Min 6 characters" minLength={6} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-mobile">Mobile (WhatsApp number)</Label>
              <Input id="add-mobile" type="tel" value={addMobile} onChange={(e) => setAddMobile(e.target.value)} placeholder="e.g. 919876543210" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-client">Client *</Label>
              <Select value={addClientId} onValueChange={setAddClientId}>
                <SelectTrigger id="add-client">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-role">Role</Label>
              <Select value={addRole} onValueChange={(v) => setAddRole(v as RoleValue)}>
                <SelectTrigger id="add-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); resetAddForm(); }} disabled={addSaving}>
              Cancel
            </Button>
            <Button onClick={handleAddProfile} disabled={addSaving}>
              {addSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profiles;
